import React, { FC, useCallback, useMemo, useState, useEffect } from 'react';
import styles from './index.module.scss';
import { INavFolder, ROOT_FOLDER } from '../context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHomeAlt, faAngleRight, faFolderOpen } from '@fortawesome/pro-light-svg-icons';
import classNames from 'classnames';
import notification from 'src/common/notification';
import {
  useUpdateFile,
  useNavFoldersContext,
  useUpdateNavFoldersContext,
  useCurrentFolderContext,
  useUpdateCurrentFolderContext,
  useUpdateFolder,
  getUpdateCacheOnFolderMove,
  getUpdateCacheOnFileMove,
  useActiveDocNode
} from 'src/models/documentsLibrary/hooks';
import { useTranslation } from 'react-i18next';
import { IDocumentsDndItem } from '../types';
import { DocsNodeType } from 'src/models/documentsLibrary/types';
import { useDrop } from 'react-dnd';
import { DndType } from 'src/types/dnd';

interface IDocumentsNavItemProps {
  navFolder: INavFolder;
  index: number;
  isLastItem: boolean;
}

export interface IUpdateVariables {}

export const DocumentsNavItem: FC<IDocumentsNavItemProps> = props => {
  const { navFolder, isLastItem, index } = props;
  const { id: navFolderId, name } = navFolder;
  const { t } = useTranslation();

  const navFolders = useNavFoldersContext();
  const updateNavFolders = useUpdateNavFoldersContext();
  const updateCurrentFolder = useUpdateCurrentFolderContext();
  const currentFolder = useCurrentFolderContext();
  const [moveFolder, { loading: moveFolderLoading }] = useUpdateFolder();
  const [moveFile, { loading: moveFileLoading }] = useUpdateFile();
  const [dropItem, setDropItem] = useState<string>('');
  const [, updateActiveDocNode] = useActiveDocNode();

  const isLoading = moveFolderLoading || moveFileLoading;

  const onDrop = useCallback(
    (item: IDocumentsDndItem) => {
      const { docsNode } = item;
      const { nodeType: itemNodeType, id: itemId, name: itemName } = docsNode;
      setDropItem(itemId);
      if (itemNodeType === DocsNodeType.FOLDER) {
        moveFolder({
          variables: { id: itemId, parentId: navFolderId, name: itemName },
          update: getUpdateCacheOnFolderMove(itemId, currentFolder.id, navFolderId)
        }).catch(() => {
          notification.error({
            description: t('Common.unknownErrorDesc'),
            message: t('Common.unknownError')
          });
        });
      } else {
        moveFile({
          variables: { id: itemId, parentId: navFolderId, name: itemName },
          update: getUpdateCacheOnFileMove(itemId, currentFolder.id, navFolderId)
        }).catch(() => {
          notification.error({
            description: t('Common.unknownErrorDesc'),
            message: t('Common.unknownError')
          });
        });
      }
    },
    [currentFolder.id, moveFile, moveFolder, navFolderId, t]
  );

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: DndType.DocumentsItem,
    canDrop() {
      return !isLastItem;
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
      item: monitor.getItem()
    }),
    drop: onDrop
  });

  const folderItem = useMemo(() => {
    return navFolders.find(_ => _.id === navFolderId) || ROOT_FOLDER;
  }, [navFolderId, navFolders]);

  const currentNavFolders = useMemo(() => {
    return navFolders.filter((_, idx) => idx <= index);
  }, [index, navFolders]);

  const onNavClick = useCallback(() => {
    updateNavFolders?.(currentNavFolders);
    updateCurrentFolder?.(folderItem);
  }, [currentNavFolders, folderItem, updateCurrentFolder, updateNavFolders]);

  const isDropOver = useMemo(() => {
    return canDrop && isOver;
  }, [canDrop, isOver]);

  useEffect(() => {
    updateActiveDocNode?.(isLoading ? dropItem : null);
  }, [dropItem, isLoading, updateActiveDocNode]);

  return (
    <li
      className={classNames(styles.navItem, {
        [styles.isLast]: isLastItem,
        [styles.isDropOver]: isDropOver,
        [styles.isCanDrop]: canDrop
      })}
      onClick={!isLastItem ? onNavClick : undefined}
      ref={drop}
    >
      {index === 0 ? (
        <FontAwesomeIcon icon={faHomeAlt} className={classNames(styles.rootIcon, styles.icon)} />
      ) : (
        <>
          <FontAwesomeIcon icon={faFolderOpen} className={classNames(styles.folderIcon, styles.icon)} />
          <span className={styles.name}>{name}</span>
        </>
      )}
      {!isLastItem && <FontAwesomeIcon icon={faAngleRight} className={classNames(styles.arrowIcon, styles.icon)} />}
    </li>
  );
};

interface IProps {}

export const DocumentsNav: FC<IProps> = () => {
  const navFolders = useNavFoldersContext();

  const navFoldersLength = useMemo(() => {
    return navFolders.length;
  }, [navFolders.length]);

  return (
    <div className={styles.docsNav}>
      <ul className={styles.navItems}>
        {navFolders.map((navFolder, index) => {
          const isLastItem = index === navFoldersLength - 1;
          return <DocumentsNavItem navFolder={navFolder} key={navFolder.id} isLastItem={isLastItem} index={index} />;
        })}
      </ul>
    </div>
  );
};

export default DocumentsNav;
